import React, { useState, useEffect } from "react";
import {
  Paper,
  TextField,
  Select,
  MenuItem,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Typography,
  TextareaAutosize,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Add as AddIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import useStyles from "../components/DashboardAuction/dashAuction.style";
import DashboardMenu from "../commonComponents/DashboardMenu";
import addAPIEndpointAPI from "../../api/adminAPI/addAPIEndpointAPI";
import fetchAPIEndpointsList from "../../api/adminAPI/fetchAPIEndpointsListAPI";
import updateAPIEndpointAPI from "../../api/adminAPI/updateAPIEndpointAPI";

const ApiConfigurationPage = () => {
  const classes = useStyles();
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editEndpoint, setEditEndpoint] = useState(null);
  const [newEndpoint, setNewEndpoint] = useState({
    method: "GET",
    description: "",
    path: "",
    isEnabled: true,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loadingStates, setLoadingStates] = useState({}); // Track loading for each endpoint

  // Simulate fetching endpoints
  useEffect(() => {
    fetchApiEndpointsList();
  }, []);

  const fetchApiEndpointsList = async () => {
    try {
      const response = await fetchAPIEndpointsList();
      setEndpoints(response.data?.endpoints || []);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch endpoints");
      setLoading(false);
    }
  };

  // Simulate toggling endpoint status
  const toggleEndpointStatus = async (id) => {
    setLoadingStates((prev) => ({ ...prev, [id]: true })); // Start loading
    try {
      const editEndpoint = endpoints.find((endpoint) => endpoint.id === id);
      const updated = await updateAPIEndpointAPI({
        id: editEndpoint.id,
        isEnabled: !editEndpoint.isEnabled,
      });

      setEndpoints((prev) =>
        prev.map((endpoint) =>
          endpoint.id === id
            ? { ...endpoint, isEnabled: !endpoint.isEnabled }
            : endpoint
        )
      );
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update endpoint",
        severity: "error",
      });
    }
    setLoadingStates((prev) => ({ ...prev, [id]: false })); // Stop loading
  };

  // Handle editing an endpoint
  const handleEdit = (endpoint) => {
    setEditEndpoint(endpoint);
    setOpenDialog(true);
  };

  // Handle saving changes to an endpoint
  const handleSave = async () => {
    try {
      const updated = await updateAPIEndpointAPI({
        id: editEndpoint.id,
        method: editEndpoint.method,
        description: editEndpoint.description,
        path: editEndpoint.path,
        isEnabled: editEndpoint.isEnabled,
      });
      setEndpoints((prev) =>
        prev.map((endpoint) =>
          endpoint.id === editEndpoint.id ? editEndpoint : endpoint
        )
      );
      setSnackbar({
        open: true,
        message: "Endpoint updated successfully",
        severity: "success",
      });
      setOpenDialog(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update endpoint",
        severity: "error",
      });
      return;
    }
  };

  // Handle adding a new endpoint
  const handleAdd = async () => {
    const addReqResponse = await addAPIEndpointAPI(newEndpoint);

    if (addReqResponse.status !== 201) {
      setSnackbar({
        open: true,
        message: "Failed to add endpoint",
        severity: "error",
      });
      return;
    }

    setEndpoints([...endpoints, { ...addReqResponse.data.endpoint }]);
    setSnackbar({
      open: true,
      message: "Endpoint added successfully",
      severity: "success",
    });
    setNewEndpoint({
      method: "GET",
      description: "",
      path: "",
      isEnabled: true,
    });
    setOpenDialog(false);
  };

  // Handle closing the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditEndpoint(null);
  };

  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className={classes.section}>
      <div className="container">
        <div className={classes.Dashboard}>
          <div className={classes.dashboardCard}>
            <DashboardMenu />

            <div className={classes.dashboardCards}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{ padding: "24px" }}
              >
                {/* Add New Endpoint Button */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: "16px",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Add New Endpoint
                  </Button>
                </Box>

                {loading ? (
                  <CircularProgress
                    style={{ display: "block", margin: "20px auto" }}
                  />
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {endpoints.map((endpoint) => (
                      <Paper
                        key={endpoint.id}
                        sx={{
                          padding: "16px",
                          borderRadius: "8px",
                          position: "relative",
                          opacity: loadingStates[endpoint.id] ? 0.7 : 1,
                          pointerEvents: loadingStates[endpoint.id]
                            ? "none"
                            : "auto",
                        }}
                      >
                        {loadingStates[endpoint.id] && (
                          <CircularProgress
                            size={24}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              {endpoint.method} {endpoint.path}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary" }}
                            >
                              {endpoint.description}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Tooltip
                              title={
                                endpoint.isEnabled
                                  ? "Disable Endpoint"
                                  : "Enable Endpoint"
                              }
                            >
                              <Switch
                                checked={endpoint.isEnabled}
                                onChange={() =>
                                  toggleEndpointStatus(endpoint.id)
                                }
                                color="primary"
                              />
                            </Tooltip>
                            <Tooltip title="Edit Endpoint">
                              <Button
                                onClick={() => handleEdit(endpoint)}
                                sx={{ textTransform: "none" }}
                              >
                                <EditIcon />
                              </Button>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}

                {/* Dialog for Editing/Adding Endpoints */}
                <Dialog
                  open={openDialog}
                  onClose={handleCloseDialog}
                  PaperProps={{
                    sx: {
                      width: "400px",
                      borderRadius: "12px",
                      padding: "16px",
                    },
                  }}
                >
                  <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>
                    {editEndpoint ? "Edit Endpoint" : "Add New Endpoint"}
                  </DialogTitle>
                  <DialogContent>
                    <Box sx={{ marginBottom: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", marginBottom: 1 }}
                      >
                        Method
                      </Typography>
                      <Select
                        fullWidth
                        value={
                          editEndpoint
                            ? editEndpoint.method
                            : newEndpoint.method
                        }
                        onChange={(e) =>
                          editEndpoint
                            ? setEditEndpoint({
                                ...editEndpoint,
                                method: e.target.value,
                              })
                            : setNewEndpoint({
                                ...newEndpoint,
                                method: e.target.value,
                              })
                        }
                        sx={{ textTransform: "none" }}
                      >
                        <MenuItem value="GET">GET</MenuItem>
                        <MenuItem value="POST">POST</MenuItem>
                        <MenuItem value="PUT">PUT</MenuItem>
                        <MenuItem value="DELETE">DELETE</MenuItem>
                        <MenuItem value="PATCH">PATCH</MenuItem>
                      </Select>
                    </Box>
                    <Box sx={{ marginBottom: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", marginBottom: 1 }}
                      >
                        Path
                      </Typography>
                      <TextField
                        fullWidth
                        value={
                          editEndpoint ? editEndpoint.path : newEndpoint.path
                        }
                        onChange={(e) =>
                          editEndpoint
                            ? setEditEndpoint({
                                ...editEndpoint,
                                path: e.target.value,
                              })
                            : setNewEndpoint({
                                ...newEndpoint,
                                path: e.target.value,
                              })
                        }
                        sx={{ textTransform: "none" }}
                      />
                    </Box>
                    <Box sx={{ marginBottom: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", marginBottom: 1 }}
                      >
                        Description
                      </Typography>
                      <TextareaAutosize
                        minRows={3}
                        value={
                          editEndpoint
                            ? editEndpoint.description
                            : newEndpoint.description
                        }
                        onChange={(e) =>
                          editEndpoint
                            ? setEditEndpoint({
                                ...editEndpoint,
                                description: e.target.value,
                              })
                            : setNewEndpoint({
                                ...newEndpoint,
                                description: e.target.value,
                              })
                        }
                        style={{
                          width: "100%",
                          padding: "8px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          fontFamily: "inherit",
                          fontSize: "0.875rem",
                        }}
                      />
                    </Box>
                    <Box sx={{ marginBottom: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", marginBottom: 1 }}
                      >
                        Enabled
                      </Typography>
                      <Switch
                        checked={
                          editEndpoint
                            ? editEndpoint.isEnabled
                            : newEndpoint.isEnabled
                        }
                        onChange={(e) =>
                          editEndpoint
                            ? setEditEndpoint({
                                ...editEndpoint,
                                isEnabled: e.target.checked,
                              })
                            : setNewEndpoint({
                                ...newEndpoint,
                                isEnabled: e.target.checked,
                              })
                        }
                      />
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleCloseDialog}
                      sx={{ textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={editEndpoint ? handleSave : handleAdd}
                      color="primary"
                      variant="contained"
                      sx={{ textTransform: "none" }}
                    >
                      {editEndpoint ? "Save" : "Add"}
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Snackbar for Notifications */}
                <Snackbar
                  open={snackbar.open}
                  autoHideDuration={3000}
                  onClose={handleCloseSnackbar}
                >
                  <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                  >
                    {snackbar.message}
                  </Alert>
                </Snackbar>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiConfigurationPage;
